import React, { useState, useEffect, useCallback, useContext } from "react";
import "split-pane-react/esm/themes/default.css";
import ActivityTimer from "components/activity-timer/ActivityTimer";
import Cookies from "js-cookie";
import { getCollectionRequest, populateEditor } from "services/apiRequests";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CodeFile, languageType } from "utils/interfaces";
import { logoutFunction, validateClient } from "services/authRequests";
import { EditorContext, RootContext } from "components/utils/Contexts";
import InactivityTimer from "components/activity-timer/InactivityTimer";
import ScratchEnvironment from "components/Editor/scratch-editor";
import { usePlatform } from "components/utils/PlatformContext";
import LoadingPanel from "components/utils/LoadingPanel";
import PythonJSEnvironment from "components/Editor/python-js-editor";
import ExternalEditorEnvironment from "components/Editor/external-editor";
import { getUserIdFromToken } from "services/userRequests";

const Editor: React.FC = () => {
  // States definition
  const [name, setName] = useState<string>("");
  const [video, setVideo] = useState<string>("");
  const [fileState, setFileState] = useState<CodeFile[]>([]);
  const [pdf, setPdf] = useState<string>("");
  const [language, setLanguage] = useState<languageType | null>(null);
  const [videoTranscript, setVideoTranscript] = useState("");
  const [AIAssistant, setAIAssistant] = useState<{
    name: string;
    img: string;
    greeting: string;
    intro: string;
  }>({ name: "", img: "", greeting: "", intro: "" });

  // Get the platform context
  const { idleTimeout, assistants, assistantImages } = usePlatform();
  const { setFeedbucketUsername, setFeedbucketEmail, setFeedbucketToken } =
    useContext(RootContext);

  // User information
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");

  // Get the url parameters
  const { moduleOrProject, id } = useParams();

  // Define a navigator state
  const navigate = useNavigate();

  // Get a state if we are receiving a state variable from another url
  const { state } = useLocation();

  /**
   * Check if the user has an access token every time we enter this view
   */
  useEffect(() => {
    validateClient().then(
      (response: { connected: boolean; navigateTo: string }) => {
        // Check if the token is validated
        if (!response.connected) return logoutFunction();
        else {
          // Load user's profile info
          getCollectionRequest("/api/users/my-profile-info").then(
            (response) => {
              // Check if the response is valid
              if (!(response && response.successful)) {
                return logoutFunction();
              }

              // Get the user
              const user = response.content;

              // And set the states
              setUserFirstName(user.firstName);
              setUserLastName(user.lastName);
              setUserEmail(user.email);
            }
          );

          // Set an editor session cookie
          Cookies.set(
            "editorSession",
            Math.floor(Math.random() * Math.pow(36, 8))
              .toString(36)
              .padStart(8, "0")
          );
        }
      }
    );
  }, []);

  // Retrieve module data from db
  const fetchModuleData = useCallback(
    async (id: string) => {
      if (typeof id !== "string") {
        console.error("Invalid moduleId:", id);
        return;
      }
      try {
        const data = await populateEditor(moduleOrProject, id);
        if (data) {
          setName(data.title);
          setVideo(data.videoUrl);
          setPdf(data.planName);
          setLanguage(data.language);
          setVideoTranscript(data.videoTranscript);
        } else {
          console.error("Invalid module data received from backend");
        }
      } catch (error) {
        console.error("Error fetching module data:", error);
      }
    },
    [moduleOrProject]
  );

  // Return to home url
  const goToDashboard = () => {
    if (state) {
      navigate("/home", {
        state: {
          // State for MainView.tsx
          activeItem: "lessons",
          // State for Lessons.tsx
          activeLesson: moduleOrProject,
          navRegister: state ? state.navRegister : [],
        },
      });
    } else {
      navigate("/home");
    }
  };

  /**
   * Get the data
   */
  useEffect(() => {
    if (id) fetchModuleData(id);
  }, [id, fetchModuleData]);

  /**
   * Define the AI assistant
   */
  useEffect(() => {
    // Check if the assistants are defined
    if (!assistants) return;

    // Check how many assistants do we have
    const numAssistants = assistants.length;

    // Check if there's at least an AI assistant defined
    if (numAssistants === 0) return;

    // If there is at least an assistant defined...
    if (id) {
      // Get a boolean indicating if the last character of the id is
      // odd or even when transforming that into an ASCII code
      const assistantIndex = id.slice(-1).charCodeAt(0) % numAssistants;

      // If it's even, display the first assistant, otherwise display the second.
      setAIAssistant({
        ...assistants[assistantIndex],
        img: assistantImages[assistantIndex],
      });
    }
  }, [id]);

  /**
   * Function used on idle time out
   */
  const onIdleTimeout = async () => {
    await logoutFunction();
  };

  /**
   * Define the feedbucket profile
   */
  useEffect(() => {
    if (userFirstName && userLastName) {
      setFeedbucketUsername(`${userFirstName} ${userLastName}`);
      setFeedbucketEmail(userEmail);
      setFeedbucketToken(getUserIdFromToken());
    }
  }, [userFirstName, userLastName, userEmail]);

  return (
    <EditorContext.Provider
      value={{
        title: name,
        userFirstName,
        userLastName,
        pdf,
        AIAssistant,
        goToDashboard,
      }}
    >
      {/* Timers */}
      <InactivityTimer timeoutMs={idleTimeout} onTimeout={onIdleTimeout} />
      <ActivityTimer type={moduleOrProject} itemId={id} intervalMs={10000} />
      {/* Language dependent components */}
      {["Python", "JavaScript"].includes(language || "") ? (
        <PythonJSEnvironment
          name={name}
          video={video}
          pdf={pdf}
        />
      ) : language === "Scratch" ? (
        <ScratchEnvironment
          name={name}
          video={video}
          pdf={pdf}
        />
      ) : ["Minecraft", "Micro:bit", "Cultural Capabilities", "Video and PDF"].includes(
          language || ""
        ) ? (
        <ExternalEditorEnvironment
          name={name}
          language={language}
          video={video}
          videoTranscript={videoTranscript}
          pdf={pdf}
          AIAssistant={AIAssistant}
          fileState={fileState}
        />
      ) : (
        <LoadingPanel />
      )}
    </EditorContext.Provider>
  );
};

export default Editor;
